import { lazy } from 'react'
import { createBrowserRouter } from 'react-router-dom'
import { Shell } from './layout/Shell'
import { Home, NotFound, Protected, RouteError, Screen } from './routeShells'
import { LoginPage } from '@/features/auth/LoginPage'
import { ModulePendingPage } from '@/features/pending/ModulePendingPage'

/* Écrans chargés à la demande : le bundle initial ne porte que la coque. */
const load = <T extends Record<string, unknown>>(f: () => Promise<T>, name: keyof T) =>
  lazy(() => f().then((m) => ({ default: m[name] as React.ComponentType })))

const DirectorDashboard = load(
  () => import('@/features/dashboard/DirectorDashboard'),
  'DirectorDashboard',
)
const ManagerDashboard = load(
  () => import('@/features/dashboard/ManagerDashboard'),
  'ManagerDashboard',
)
const StationsPage = load(() => import('@/features/stations/StationsPage'), 'StationsPage')
const StationDetailPage = load(
  () => import('@/features/stations/StationDetailPage'),
  'StationDetailPage',
)
const StationFormPage = load(() => import('@/features/stations/StationFormPage'), 'StationFormPage')
const ShiftsPage = load(() => import('@/features/shifts/ShiftsPage'), 'ShiftsPage')
const ShiftDetailPage = load(() => import('@/features/shifts/ShiftDetailPage'), 'ShiftDetailPage')
const OpenShiftPage = load(() => import('@/features/shifts/OpenShiftPage'), 'OpenShiftPage')
const CloseShiftPage = load(() => import('@/features/shifts/CloseShiftPage'), 'CloseShiftPage')
const TanksPage = load(() => import('@/features/tanks/TanksPage'), 'TanksPage')
const TankDetailPage = load(() => import('@/features/tanks/TankDetailPage'), 'TankDetailPage')
const GaugingPage = load(() => import('@/features/tanks/GaugingPage'), 'GaugingPage')
const StationAssetsPage = load(
  () => import('@/features/stations/StationAssetsPage'),
  'StationAssetsPage',
)
const FuelPricesPage = load(() => import('@/features/stations/FuelPricesPage'), 'FuelPricesPage')
const VouchersPage = load(() => import('@/features/vouchers/VouchersPage'), 'VouchersPage')
const ReportsPage = load(() => import('@/features/reports/ReportsPage'), 'ReportsPage')
const ShopPage = load(() => import('@/features/shop/ShopPage'), 'ShopPage')
const ProcurementPage = load(
  () => import('@/features/procurement/ProcurementPage'),
  'ProcurementPage',
)
const PurchaseOrderFormPage = load(
  () => import('@/features/procurement/PurchaseOrderFormPage'),
  'PurchaseOrderFormPage',
)
const DeliveryFormPage = load(
  () => import('@/features/procurement/DeliveryFormPage'),
  'DeliveryFormPage',
)
const CustomersPage = load(() => import('@/features/customers/CustomersPage'), 'CustomersPage')
const CustomerDetailPage = load(
  () => import('@/features/customers/CustomerDetailPage'),
  'CustomerDetailPage',
)
const CustomerFormPage = load(
  () => import('@/features/customers/CustomerFormPage'),
  'CustomerFormPage',
)
const InvoiceFormPage = load(
  () => import('@/features/customers/InvoiceFormPage'),
  'InvoiceFormPage',
)
const AnomaliesPage = load(() => import('@/features/risk/AnomaliesPage'), 'AnomaliesPage')
const AnomalyDetailPage = load(
  () => import('@/features/risk/AnomalyDetailPage'),
  'AnomalyDetailPage',
)
const AlertsPage = load(() => import('@/features/risk/AlertsPage'), 'AlertsPage')
const MaintenancePage = load(
  () => import('@/features/maintenance/MaintenancePage'),
  'MaintenancePage',
)
const EquipmentFormPage = load(
  () => import('@/features/maintenance/EquipmentFormPage'),
  'EquipmentFormPage',
)
const MaintenanceRecordFormPage = load(
  () => import('@/features/maintenance/MaintenanceRecordFormPage'),
  'MaintenanceRecordFormPage',
)
const InspectionsPage = load(
  () => import('@/features/inspections/InspectionsPage'),
  'InspectionsPage',
)
const InspectionFormPage = load(
  () => import('@/features/inspections/InspectionFormPage'),
  'InspectionFormPage',
)
const UsersPage = load(() => import('@/features/admin/UsersPage'), 'UsersPage')
const UserFormPage = load(() => import('@/features/admin/UserFormPage'), 'UserFormPage')
const RolesPage = load(() => import('@/features/admin/RolesPage'), 'RolesPage')
const RoleFormPage = load(() => import('@/features/admin/RoleFormPage'), 'RoleFormPage')
const ModulesPage = load(() => import('@/features/admin/ModulesPage'), 'ModulesPage')
const AuditLogPage = load(() => import('@/features/admin/AuditLogPage'), 'AuditLogPage')
const MobilePage = load(() => import('@/features/mobile/MobilePage'), 'MobilePage')

const VoucherPrintPage = load(
  () => import('@/features/vouchers/VoucherPrintPage'),
  'VoucherPrintPage',
)

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <RouteError /> },

  /*
   * Planche de vignettes : hors de la coque de l'application. Ce qui
   * s'affiche est ce qui sortira de l'imprimante, sans menu ni cadre.
   */
  {
    path: '/vouchers/batches/:batchId/print',
    element: (
      <Protected>
        <VoucherPrintPage />
      </Protected>
    ),
    errorElement: <RouteError />,
  },
  {
    path: '/',
    element: (
      <Protected>
        <Shell />
      </Protected>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Home /> },
      {
        path: 'dashboard',
        element: (
          <Screen>
            <DirectorDashboard />
          </Screen>
        ),
      },
      {
        path: 'my-station',
        element: (
          <Screen>
            <ManagerDashboard />
          </Screen>
        ),
      },

      {
        path: 'stations',
        element: (
          <Screen>
            <StationsPage />
          </Screen>
        ),
      },
      {
        path: 'stations/new',
        element: (
          <Screen>
            <StationFormPage />
          </Screen>
        ),
      },
      {
        path: 'stations/:stationId',
        element: (
          <Screen>
            <StationDetailPage />
          </Screen>
        ),
      },
      {
        path: 'stations/:stationId/edit',
        element: (
          <Screen>
            <StationFormPage />
          </Screen>
        ),
      },

      {
        path: 'shifts',
        element: (
          <Screen module="operations">
            <ShiftsPage />
          </Screen>
        ),
      },
      {
        path: 'shifts/new',
        element: (
          <Screen module="operations">
            <OpenShiftPage />
          </Screen>
        ),
      },
      {
        path: 'shifts/:shiftId',
        element: (
          <Screen module="operations">
            <ShiftDetailPage />
          </Screen>
        ),
      },
      {
        path: 'shifts/:shiftId/close',
        element: (
          <Screen module="operations">
            <CloseShiftPage />
          </Screen>
        ),
      },

      {
        path: 'tanks',
        element: (
          <Screen module="inventory">
            <TanksPage />
          </Screen>
        ),
      },
      {
        path: 'tanks/:tankId',
        element: (
          <Screen module="inventory">
            <TankDetailPage />
          </Screen>
        ),
      },
      {
        path: 'tanks/:tankId/gauge',
        element: (
          <Screen module="inventory">
            <GaugingPage />
          </Screen>
        ),
      },

      {
        path: 'equipment',
        element: (
          <Screen module="inventory">
            <StationAssetsPage />
          </Screen>
        ),
      },
      {
        path: 'prices',
        element: (
          <Screen module="inventory">
            <FuelPricesPage />
          </Screen>
        ),
      },
      {
        path: 'vouchers',
        element: (
          <Screen module="vouchers">
            <VouchersPage />
          </Screen>
        ),
      },

      {
        path: 'procurement',
        element: (
          <Screen module="procurement">
            <ProcurementPage />
          </Screen>
        ),
      },
      {
        path: 'procurement/orders/new',
        element: (
          <Screen module="procurement">
            <PurchaseOrderFormPage />
          </Screen>
        ),
      },
      {
        path: 'procurement/deliveries/new',
        element: (
          <Screen module="procurement">
            <DeliveryFormPage />
          </Screen>
        ),
      },

      {
        path: 'customers',
        element: (
          <Screen module="credit">
            <CustomersPage />
          </Screen>
        ),
      },
      {
        path: 'customers/new',
        element: (
          <Screen module="credit">
            <CustomerFormPage />
          </Screen>
        ),
      },
      {
        path: 'customers/invoices/new',
        element: (
          <Screen module="credit">
            <InvoiceFormPage />
          </Screen>
        ),
      },
      {
        path: 'customers/:customerId',
        element: (
          <Screen module="credit">
            <CustomerDetailPage />
          </Screen>
        ),
      },
      {
        path: 'customers/:customerId/edit',
        element: (
          <Screen module="credit">
            <CustomerFormPage />
          </Screen>
        ),
      },

      {
        path: 'anomalies',
        element: (
          <Screen module="risk">
            <AnomaliesPage />
          </Screen>
        ),
      },
      {
        path: 'anomalies/:anomalyId',
        element: (
          <Screen module="risk">
            <AnomalyDetailPage />
          </Screen>
        ),
      },
      {
        path: 'alerts',
        element: (
          <Screen module="risk">
            <AlertsPage />
          </Screen>
        ),
      },

      {
        path: 'maintenance',
        element: (
          <Screen module="maintenance">
            <MaintenancePage />
          </Screen>
        ),
      },
      {
        path: 'maintenance/equipment/new',
        element: (
          <Screen module="maintenance">
            <EquipmentFormPage />
          </Screen>
        ),
      },
      {
        path: 'maintenance/records/new',
        element: (
          <Screen module="maintenance">
            <MaintenanceRecordFormPage />
          </Screen>
        ),
      },

      {
        path: 'inspections',
        element: (
          <Screen module="inspections">
            <InspectionsPage />
          </Screen>
        ),
      },
      {
        path: 'inspections/new',
        element: (
          <Screen module="inspections">
            <InspectionFormPage />
          </Screen>
        ),
      },

      {
        path: 'shop',
        element: (
          <Screen module="shop">
            <ShopPage />
          </Screen>
        ),
      },
      {
        path: 'reports',
        element: (
          <Screen module="reports">
            <ReportsPage />
          </Screen>
        ),
      },
      {
        path: 'loyalty',
        element: <ModulePendingPage module="loyalty" scope="le programme de fidélité" />,
      },
      {
        path: 'financing',
        element: <ModulePendingPage module="financing" scope="le financement et l'assurance" />,
      },
      {
        path: 'integrations',
        element: (
          <ModulePendingPage
            module="integrations"
            scope="les clés d'API, webhooks et connecteurs"
          />
        ),
      },

      {
        path: 'admin/users',
        element: (
          <Screen>
            <UsersPage />
          </Screen>
        ),
      },
      {
        path: 'admin/users/new',
        element: (
          <Screen>
            <UserFormPage />
          </Screen>
        ),
      },
      {
        path: 'admin/users/:userId/edit',
        element: (
          <Screen>
            <UserFormPage />
          </Screen>
        ),
      },
      {
        path: 'admin/roles',
        element: (
          <Screen>
            <RolesPage />
          </Screen>
        ),
      },
      {
        path: 'admin/roles/new',
        element: (
          <Screen>
            <RoleFormPage />
          </Screen>
        ),
      },
      {
        path: 'admin/roles/:roleId/edit',
        element: (
          <Screen>
            <RoleFormPage />
          </Screen>
        ),
      },
      {
        path: 'admin/modules',
        element: (
          <Screen>
            <ModulesPage />
          </Screen>
        ),
      },
      {
        path: 'admin/audit',
        element: (
          <Screen>
            <AuditLogPage />
          </Screen>
        ),
      },

      {
        path: 'mobile',
        element: (
          <Screen>
            <MobilePage />
          </Screen>
        ),
      },
      { path: '*', element: <NotFound /> },
    ],
  },
])
